import atlasJson from "../../ui/generated/ui-atlas-0.json";
import atlasPng from "../../ui/generated/ui-atlas-0.png";
import type { WorldUiModule } from "./hook.ts";

/**
 * The UI atlas on the canvas (ADR-061 decision 8 as amended): the page and Phaser JSON hash that `bun run
 * ui:build` commits under `src/ui/generated/`, bundled with the client and loaded under the key `ui`, apart
 * from the world atlases and the `AssetManifest`. Every canvas surface draws its frames, marks and icons from
 * this texture by the atlas's frame names (`ui/frame/window`, `ui/flag/shield_16`, `ui/icon/menu_24`), and a
 * nine-slice frame carries its `scale9Borders`, so a `NineSlice` built from it needs only its size. The DOM
 * draws the same pixels through `ui-atlas.css`, generated from the same JSON.
 *
 * The build packs everything into one page while it fits; `tests/ui-atlas.test.ts` fails when a second page
 * appears, so this module is extended before any frame goes missing on the canvas.
 *
 * @example
 * ```ts
 * const frame = new Phaser.GameObjects.NineSlice(scene, 8, 8, UI_ATLAS_KEY, "ui/frame/window", 48, 32).setOrigin(0);
 * context.add(frame);
 * ```
 */

/**
 * The texture key the UI atlas loads under.
 */
export const UI_ATLAS_KEY = "ui";

/**
 * Loads the UI atlas before any surface is created. It is listed first in `modules.ts`, and the gallery's
 * pages list it before their own surfaces, so every surface finds the texture in `create`.
 */
export const uiAtlas: WorldUiModule = {
  id: "ui-atlas",
  /**
   * Queues the atlas page with its JSON hash, which the bundle already holds, so only the image is fetched.
   *
   * @param load The scene's loader.
   */
  preload(load) {
    if (!load.textureManager.exists(UI_ATLAS_KEY)) {
      load.atlas(UI_ATLAS_KEY, atlasPng, atlasJson);
    }
  },
  /**
   * Adds nothing: the atlas is a texture the other surfaces draw from.
   *
   * @returns An empty surface.
   */
  create() {
    return {};
  },
};
